import { useFetch, useMakeFetch } from '@chipinside/fetcher'
import { MarkdownView } from '@chipinside/markdown'
import { Translate } from '@chipinside/translate'
import { Alert, Box, Stack } from '@mui/material'
import { Button, ButtonProps } from '@mui/material'
import DialogActions from '@mui/material/DialogActions'
import Typography from '@mui/material/Typography'
import { useState } from 'react'

import ResponseDialog from '#/components/Modal/ResponseDialog'
import { getHeaderCustomization } from '#/components/Modal/ResponseDialog/content'

interface RemoteForm {
  buttons?: { submit?: { text?: string } }
  fields?: { type: string; value?: string; variation?: string }[]
  save?: { controller: string; method: string }
  title?: string
}

interface SubmitButtonProps {
  disabled?: boolean
  submitButton?: { title?: string; level?: string }
  level?: string
  method: 'create' | 'update' | 'delete'
  onSubmit: () => void
}

export function ConfirmDialog(props) {
  const {
    afterSuccess,
    alert,
    controller: formController,
    onClick,
    onClose,
    submitButton: localSubmitButton,
    subtitle,
  } = props

  const [open, setOpen] = useState(false)

  const { data: remoteForm, isLoading: isLoadingForm } = useFetch<RemoteForm>({
    controller: formController,
  })

  const remoteMessage = remoteForm?.fields?.find(field => field.type === 'message')

  const {
    level = remoteMessage?.variation,
    message = remoteMessage?.value,
    save = remoteForm?.save,
    title = remoteForm?.title,
  } = props

  const submitButton = {
    ...localSubmitButton,
    title: localSubmitButton?.title ?? remoteForm?.buttons?.submit?.text,
  }
  const [{ code, data, error, isLoading }, makeRequest] = useMakeFetch()

  const handleClose = () => {
    setOpen(false)
    onClose?.()
  }
  const { controller, manipulateParams, method } = save || {}

  const handleSubmit = (values: Record<string, unknown> | null) => {
    setOpen(true)
    if (controller) {
      makeRequest({
        controller,
        method,
        params: manipulateParams?.(values) ?? values,
      })
    }
    onClick?.(data)
  }

  // Ações para conflito (409)
  const onConflictActions = () => {
    const { extra } = (error as any) ?? {}

    if (code === 409 && extra) {
      const confirmButton = () => (
        <Button
          key={title}
          variant={'contained'}
          color="warning"
          onClick={() => handleSubmit({ [extra]: true })}
        >
          {Translate({ messageKey: 'confirm_item' })}
        </Button>
      )

      return [confirmButton]
    }
  }

  const codeMap = {
    delete: 500,
    error: 500,
    info: 100,
    neutral: 300,
    post: 200,
    put: 400,
    success: 200,
    warning: 400,
  }

  const { color, icon } = getHeaderCustomization(codeMap[level ?? method])

  function SubmitButton({ disabled, level, method, onSubmit, submitButton }: SubmitButtonProps) {
    const color = submitButton?.level || level || (method === 'delete' ? 'error' : 'warning')

    const title =
      submitButton?.title ||
      (method === 'delete'
        ? Translate({ messageKey: 'remove_item' })
        : Translate({ messageKey: 'confirm_item' }))

    return (
      <Button
        disabled={disabled}
        onClick={onSubmit}
        variant="contained"
        color={color as ButtonProps['color']}
      >
        {title}
      </Button>
    )
  }

  return (
    <>
      <Stack spacing={2}>
        <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center' }}>
          <Box
            sx={{
              alignItems: 'center',
              color,
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'center',
            }}
          >
            {icon}
            <Typography sx={{ color, fontSize: 24, fontWeight: '500' }}>{title}</Typography>
            <Typography sx={{ color: 'grey.700', whiteSpace: 'pre-line' }}>{subtitle}</Typography>
          </Box>
          {message && (
            <Typography component="div" sx={{ color: 'grey.800', fontSize: 16 }}>
              <MarkdownView>{message}</MarkdownView>
            </Typography>
          )}
          {alert && (
            <Alert severity={alert.level ?? 'info'}>
              <MarkdownView style={{ color: 'inherit' }}>{alert.text}</MarkdownView>
            </Alert>
          )}
        </Stack>
        <DialogActions
          sx={{
            marginBottom: '-16px !important',
            marginRight: '-16px !important',
          }}
        >
          <Button onClick={handleClose} variant="outlined" color="secondary">
            {Translate({ messageKey: 'close' })}
          </Button>
          <SubmitButton
            disabled={isLoadingForm}
            submitButton={submitButton}
            level={level}
            method={method}
            onSubmit={() => handleSubmit(null)}
          />
        </DialogActions>
      </Stack>
      <ResponseDialog
        open={open}
        loading={isLoading}
        onClose={handleClose}
        data={data}
        error={error}
        code={code}
        onConflictActions={onConflictActions}
        afterSuccess={() => {
          afterSuccess?.()
        }}
      />
    </>
  )
}
